import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SchnellregistrierungModal from '../components/dashboard/SchnellregistrierungModal';

/* ── JWT helpers ──────────────────────────────────────────────────────────── */

function decodeToken(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function getAuthHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };
}

/* ── Athlet-Suche ─────────────────────────────────────────────────────────── */

function AthletenSuche({ turnierId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `/api/wettkaempfer?search=${encodeURIComponent(query)}&turnier_id=${turnierId}`,
          { headers: getAuthHeaders() }
        );
        const data = await res.json();
        setResults(Array.isArray(data?.data) ? data.data.slice(0, 15) : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, turnierId]);

  return (
    <div style={s.searchWrap}>
      <input
        style={s.searchInput}
        placeholder="Name suchen…"
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      {searching && <div style={s.searchHint}>Suche…</div>}
      {results.length > 0 && (
        <table style={s.resultsTable}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Verein</th>
              <th style={s.th}>Jahrgang</th>
              <th style={s.th}>Gewicht</th>
            </tr>
          </thead>
          <tbody>
            {results.map(a => (
              <tr key={a.id ?? a.wettkaempfer_id} style={s.tr}>
                <td style={s.td}>{a.vorname} {a.nachname}</td>
                <td style={s.tdMuted}>{a.verein_name || '—'}</td>
                <td style={s.tdMuted}>
                  {a.geburtsdatum ? new Date(a.geburtsdatum).getFullYear() : '—'}
                </td>
                <td style={s.tdMuted}>{a.gewicht ? `${a.gewicht} kg` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!searching && query.length >= 2 && results.length === 0 && (
        <div style={s.searchHint}>Keine Ergebnisse.</div>
      )}
    </div>
  );
}

/* ── Letzte Anmeldungen ───────────────────────────────────────────────────── */

function LetzteAnmeldungen({ turnierId }) {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!turnierId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/wettkaempfer?turnier_id=${turnierId}&limit=20&sort=desc`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setAthletes(Array.isArray(data?.data) ? data.data.slice(0, 20) : []);
    } catch {
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  }, [turnierId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={s.sectionHeaderRow}>
        <span style={s.sectionTitle}>Letzte Anmeldungen</span>
        <button onClick={load} disabled={loading} style={s.refreshBtn}>
          {loading ? '…' : '↻ Aktualisieren'}
        </button>
      </div>
      {loading && <div style={s.loadingHint}>Lade…</div>}
      {!loading && athletes.length === 0 && (
        <div style={s.emptyHint}>Noch keine Anmeldungen vorhanden.</div>
      )}
      {athletes.length > 0 && (
        <div style={s.tableWrap}>
          <table style={s.resultsTable}>
            <thead>
              <tr>
                <th style={s.th}>#</th>
                <th style={s.th}>Name</th>
                <th style={s.th}>Verein</th>
                <th style={s.th}>Kategorie</th>
              </tr>
            </thead>
            <tbody>
              {athletes.map((a, i) => (
                <tr key={a.id ?? a.wettkaempfer_id} style={s.tr}>
                  <td style={{ ...s.tdMuted, width: 36 }}>{i + 1}</td>
                  <td style={s.td}>{a.vorname} {a.nachname}</td>
                  <td style={s.tdMuted}>{a.verein_name || '—'}</td>
                  <td style={s.tdMuted}>{a.division_name || a.kategorie || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Haupt-Dashboard ─────────────────────────────────────────────────────── */

export default function RegStaffDashboard() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [turnier, setTurnier] = useState(null);
  const [activePanel, setActivePanel] = useState(null); // 'schnellreg' | 'suche'
  const [showSchnellreg, setShowSchnellreg] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [todayCount, setTodayCount] = useState(0);

  /* Auth-Check */
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setUnauthorized(true); return; }
    const p = decodeToken(token);
    if (!p || p.role !== 'registration') { setUnauthorized(true); return; }
    setPayload(p);
  }, []);

  /* Turnier laden */
  useEffect(() => {
    if (!payload?.turnier_id) return;
    fetch(`/api/turniere/${payload.turnier_id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setTurnier(data); })
      .catch(() => {});
  }, [payload]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/reg-login');
  };

  const incrementToday = useCallback(() => {
    setTodayCount(prev => prev + 1);
  }, []);

  const isTurnierPast = useMemo(() => {
    if (!turnier?.datum) return false;
    const d = new Date(turnier.datum);
    d.setHours(23, 59, 59, 999);
    return new Date() > d;
  }, [turnier?.datum]);

  /* Unauthorized */
  if (unauthorized) {
    return (
      <div style={s.page}>
        <div style={s.unauthCard}>
          <div style={s.unauthIcon}>🔒</div>
          <p style={s.unauthText}>Kein Zugriff. Bitte einloggen.</p>
          <button style={s.unauthBtn} onClick={() => navigate('/reg-login')}>
            Zum Login
          </button>
        </div>
      </div>
    );
  }

  if (!payload) return null;

  const turnierName = turnier?.name || `Turnier #${payload.turnier_id}`;

  return (
    <div style={s.page}>
      {/* Header */}
      <header style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.headerLogo}>⚡</span>
          <div>
            <div style={s.headerTitle}>Registrierung</div>
            <div style={s.headerSub}>{turnierName}</div>
          </div>
        </div>
        <div style={s.headerRight}>
          <span style={s.headerName}>{payload.name || payload.email || 'Helfer'}</span>
          {todayCount > 0 && (
            <span style={{ ...s.badge, background: '#4ade80', color: '#0d0d0d' }}>
              ✓ {todayCount} heute
            </span>
          )}
          <button style={s.logoutBtn} onClick={handleLogout}>Logout</button>
        </div>
      </header>

      {/* Aktions-Karten */}
      <main style={s.main}>
        {isTurnierPast && (
          <div style={{
            background: 'rgba(251,191,36,0.12)',
            border: '1px solid #fbbf24',
            borderRadius: 8,
            padding: '10px 16px',
            margin: '0 0 12px',
            color: '#fbbf24',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            ⚠️ Das Turnier <strong>{turnier.name}</strong> ist bereits abgeschlossen. Registrierungen sind nur noch eingeschränkt möglich.
          </div>
        )}
        <section style={s.cardGrid}>
          {/* Schnellregistrierung */}
          <button
            style={{ ...s.actionCard, ...(showSchnellreg ? s.actionCardActive : {}) }}
            onClick={() => { setShowSchnellreg(true); setActivePanel(null); }}
          >
            <span style={s.actionIcon}>⚡</span>
            <span style={s.actionLabel}>Schnellregistrierung</span>
            <span style={s.actionSub}>Athlet direkt anmelden</span>
          </button>

          {/* Athleten-Suche */}
          <button
            style={{ ...s.actionCard, ...(activePanel === 'suche' ? s.actionCardActive : {}) }}
            onClick={() => setActivePanel(activePanel === 'suche' ? null : 'suche')}
          >
            <span style={s.actionIcon}>🔍</span>
            <span style={s.actionLabel}>Athleten-Suche</span>
            <span style={s.actionSub}>Angemeldete finden</span>
          </button>
        </section>

        {/* Suche-Panel */}
        {activePanel === 'suche' && (
          <section style={s.panel}>
            <div style={s.panelHeader}>
              <span style={s.panelTitle}>Athleten-Suche</span>
              <button style={s.panelClose} onClick={() => setActivePanel(null)}>✕</button>
            </div>
            <AthletenSuche turnierId={payload.turnier_id} />
          </section>
        )}

        {/* Letzte Anmeldungen */}
        <section style={s.panel}>
          <LetzteAnmeldungen turnierId={payload.turnier_id} />
        </section>
      </main>

      {/* Schnellregistrierungs-Modal */}
      {showSchnellreg && turnier && (
        <SchnellregistrierungModal
          turnier={turnier}
          onClose={() => setShowSchnellreg(false)}
          onRegistered={incrementToday}
        />
      )}
    </div>
  );
}

/* ── Inline-Styles ────────────────────────────────────────────────────────── */

const s = {
  page: {
    minHeight: '100vh',
    background: '#0d0d0d',
    color: '#fff',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },

  /* Header */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    background: '#141414',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    flexWrap: 'wrap',
    gap: '0.5rem',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
  },
  headerLogo: {
    fontSize: '1.6rem',
    lineHeight: 1,
    filter: 'drop-shadow(0 0 6px rgba(255,200,0,0.5))',
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 800,
    color: '#fff',
    lineHeight: 1.1,
  },
  headerSub: {
    fontSize: '0.72rem',
    color: 'rgba(255,255,255,0.45)',
    marginTop: 2,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  headerName: {
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.6)',
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6,
    color: 'rgba(255,255,255,0.7)',
    padding: '0.35rem 0.75rem',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },

  /* Main */
  main: {
    flex: 1,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    maxWidth: 900,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },

  /* Aktions-Karten */
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.75rem',
  },
  actionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '1.4rem 1rem',
    background: '#161616',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, background 0.2s ease',
    color: '#fff',
    textAlign: 'center',
  },
  actionCardActive: {
    borderColor: 'rgba(255,51,51,0.45)',
    background: 'rgba(255,51,51,0.06)',
  },
  actionIcon: {
    fontSize: '2rem',
    lineHeight: 1,
  },
  actionLabel: {
    fontSize: '0.95rem',
    fontWeight: 700,
  },
  actionSub: {
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.45)',
  },

  /* Panel */
  panel: {
    background: '#141414',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12,
    padding: '1rem',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
  },
  panelTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#fff',
  },
  panelClose: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0.2rem 0.4rem',
  },

  /* Section */
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
  },
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#fff',
  },
  refreshBtn: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6,
    color: 'rgba(255,255,255,0.65)',
    padding: '0.3rem 0.65rem',
    fontSize: '0.78rem',
    cursor: 'pointer',
  },

  /* Suche */
  searchWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  searchInput: {
    background: '#0d0d0d',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    padding: '0.65rem 0.9rem',
    color: '#fff',
    fontSize: '0.95rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  searchHint: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.35)',
    padding: '0.25rem 0',
  },

  /* Tabellen */
  tableWrap: {
    overflowX: 'auto',
  },
  resultsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.85rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.45rem 0.6rem',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.45)',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  td: {
    padding: '0.5rem 0.6rem',
    color: '#fff',
    fontWeight: 500,
  },
  tdMuted: {
    padding: '0.5rem 0.6rem',
    color: 'rgba(255,255,255,0.5)',
  },

  /* Sonstiges */
  loadingHint: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: '0.85rem',
    padding: '0.5rem 0',
  },
  emptyHint: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: '0.85rem',
    padding: '0.75rem 0',
    textAlign: 'center',
  },

  /* Badge */
  badge: {
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: '0.75rem',
    fontWeight: 700,
  },

  /* Unauthorized */
  unauthCard: {
    background: '#161616',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: '3rem 2rem',
    textAlign: 'center',
    maxWidth: 360,
    margin: '30vh auto 0',
  },
  unauthIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  unauthText: {
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '1.5rem',
    fontSize: '0.95rem',
  },
  unauthBtn: {
    background: 'linear-gradient(135deg, #ff3333, #cc0000)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '0.7rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
};
